import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { TrendingUp, Sparkles, Info } from "lucide-react";

export default function PriceHint({ requestId, onSuggest, currentPrice }) {
  const { t } = useLang();
  const [hint, setHint] = useState(null);

  useEffect(() => {
    if (!requestId) return;
    api.get(`/requests/${requestId}/price-hint`).then(r => setHint(r.data)).catch(() => {});
  }, [requestId]);

  if (!hint) return null;
  const cp = parseFloat(currentPrice || 0);
  const budget = hint.customer_budget;
  let tone = "zinc";
  let label = "";

  if (cp && budget) {
    const ratio = cp / budget;
    if (ratio > 1) { tone = "rose"; label = t("aboveBudget"); }
    else if (ratio > 0.95) { tone = "amber"; label = t("nearBudget"); }
    else if (ratio < 0.5) { tone = "amber"; label = t("veryCheap"); }
    else { tone = "emerald"; label = t("competitiveVsBudget"); }
  }

  const tones = {
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-700",
    amber: "border-amber-200 bg-amber-50/50 text-amber-800",
    rose: "border-rose-200 bg-rose-50/50 text-rose-700",
    zinc: "border-zinc-200 bg-zinc-50 text-zinc-700",
  };

  const applySuggestion = () => onSuggest?.(hint.suggested);

  return (
    <div className={`rounded-2xl border p-3.5 text-sm ${tones[tone]}`} data-testid="price-hint">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-white grid place-items-center shrink-0 shadow-sm">
          <TrendingUp className="h-4 w-4 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-zinc-900">
            {hint.suggested != null ? (
              <>{t("suggestedPrice")} <span className="text-emerald-700">{hint.suggested} {t("saudiRiyal")}</span></>
            ) : (
              t("setYourPrice")
            )}
          </div>
          <div className="text-xs text-zinc-600 mt-0.5">
            {hint.accepted_avg != null
              ? <>{t("avgAccepted")} {hint.accepted_count > 1 ? `(${hint.accepted_count} ${t("offers")})` : ""}: <b>{hint.accepted_avg} {t("saudiRiyal")}</b> · </>
              : null}
            {t("customerBudget")}: <b>{budget || "—"} {t("saudiRiyal")}</b>
          </div>
          {label && (
            <div className="text-[11px] mt-1 inline-flex items-center gap-1 font-medium">
              <Info className="h-3 w-3" /> {label}
            </div>
          )}
          {hint.suggested != null && (
            <button
              type="button"
              data-testid="apply-suggestion"
              onClick={applySuggestion}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-800 underline-offset-4 hover:underline"
            >
              <Sparkles className="h-3.5 w-3.5" /> {t("useThisPrice")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
